/**
 * F5 store phone strings (English), spread into en.ts. Kept in their own module so the F4 gate
 * strings can grow in en.ts in parallel. The tile titles/hints (store.load … store.countHint) stay
 * in en.ts. Hindi: store.hi.ts (typed to have every key here).
 */
export const storeEn = {
  'store.devSeed': 'Load store demo data',
  'store.transfer': 'Send to another site',
  'store.transferHint': 'Load a vehicle to a godown or processing shed',
  'store.declaration': 'Storage declaration',
  'store.declarationHint': 'No-fumigant note for a bay',
  'store.stock': 'Stock on hand',
  'store.stockHint': 'Places, lots, trips, label check',
  'store.redTie': 'Red-tie bags',
  'store.redTieHint': 'Scan bags of ordinary lots',

  'store.yes': 'Yes',
  'store.no': 'No',
  'store.saveFailed': 'Could not save on the phone',
  'store.removeBag': 'Remove bag',
  'store.lots': 'Lots',
  'store.lot': 'Lot',
  'store.bags': 'Bags',
  'store.samples': 'Samples',
  'store.kgAtGate': 'kg at the gate',
  'store.nOfM': '{n} of {m}',
  'store.nOfMOn': '{n} of {m} on',
  'store.nOfMHere': '{n} of {m} here',
  'store.bagsN': '{n} {n:bag|bags}',
  'store.availableN': '{n} available',
  'store.heldN': '{n} on hold',
  'store.kgNowShort': 'now {kg}',
  'store.kgGateShort': 'gate {kg}',
  'store.daysHere': '{days} {days:day|days} here',
  'store.refusedReported': '{count} refused {count:tag|tags} will be reported',
  'store.up': 'Up one level',
  'store.lotsHere': 'Lots at this place',
  'store.onDockShort': 'On the dock',
  'store.crop': 'Crop',
  'store.stream': 'Stream',
  'store.brand': 'Brand (main)',
  'store.hold': 'Hold',
  'store.holdNone': 'None',
  'store.holdExplain':
    'On hold: it keeps its place and is not available until a supervisor clears it.',
  'store.lotMissing': 'Missing',
  'store.onTrip': 'On trip {code}',
  'store.elsewhere': 'Another place',
  'store.lotNotHere': 'This tag is not in this store’s list. Update the store list with network.',
  'store.lotLookupHint': 'Scan any tag of the lot',
  'store.noTrips': 'No vehicle on the road for this place.',
  'store.tripFrom': 'from {from}',
  'store.tripTo': 'to {to}',
  'store.code': 'Code',
  'store.level': 'Level',
  'store.use': 'Use',
  'store.active': 'Active',
  'store.labelCheckHint': 'Scan a newly stuck label',
  'store.labelCheckConfirm': 'Check that this name matches the place where the label is stuck.',
  'store.stockTab.places': 'Places',
  'store.stockTab.lot': 'Lot',
  'store.stockTab.trips': 'Trips',
  'store.stockTab.label': 'Label',

  'store.cache.age': 'Store list downloaded {when}',
  'store.cache.stale': 'The store list is more than a day old',
  'store.cache.expired': 'The store list is very old — update it as soon as you have network',
  'store.cache.update': 'Update store list',
  'store.cache.needsNetwork': 'Needs network to update',
  'store.cache.updated': 'Store list updated',
  'store.cache.failed': 'Could not update the store list',
  'store.cache.missingTitle': 'No store list on this phone',
  'store.cache.missingBody':
    'Download the places, lots and trips for this site once with network; then everything works offline.',

  'store.badge.hold': 'On hold',
  'store.badge.ordinary': 'Ordinary',
  'store.badge.available': 'Available',
  'store.badge.inTransit': 'On the road',
  'store.badge.notAvailable': 'Not available',
  'store.badge.redTie': 'Needs red tie',
  'store.badge.notSynced': 'Not synced',
  'store.badge.heldAtOffice': 'Held by office',
  'store.badge.syncProblem': 'Sync problem',

  'store.decl.clean': 'Declared clean',
  'store.decl.renewSoon': 'Renew in {days} {days:day|days}',
  'store.decl.notClean': 'Not clean',
  'store.decl.expired': 'Declaration expired',
  'store.decl.oldForm': 'Old form — sign again',
  'store.decl.none': 'No declaration',

  'store.role.STORAGE': 'Storage',
  'store.role.CONVENTIONAL': 'Red-tie bay',
  'store.role.HOLD_AREA': 'Hold area',
  'store.role.SAMPLE_STORE': 'Sample store',
  'store.role.DOCK': 'Dock',

  'store.refusal.DUPLICATE': 'Already scanned',
  'store.refusal.TAG_WRONG_KIND': 'Wrong kind of code here',
  'store.refusal.TAG_NOT_BOUND': 'This tag is not on any bag',
  'store.refusal.TAG_VOID': 'Void tag — put the bag aside; it will be reported',
  'store.refusal.TAG_RETIRED': 'Retired tag — it will be reported',
  'store.refusal.TAG_UNKNOWN': 'Unknown tag — it will be reported',
  'store.refusal.NOT_AT_ORIGIN': 'This bag is not staged here',
  'store.refusal.ALREADY_LOADED': 'This bag is already on another trip',
  'store.refusal.VEHICLE_NOT_CLEAN': 'Vehicle bed not clean: only ordinary (red-tie) bags',
  'store.refusal.NOT_STORED': 'This bag is not in store',
  'store.refusal.LEVEL_NOT_ALLOWED': 'Scan a bay or spot label',
  'store.refusal.LOCATION_INACTIVE': 'This place is closed',
  'store.refusal.OTHER_SITE': 'Another godown — that needs a vehicle transfer',
  'store.refusal.NOT_DECLARED': 'Bay has no clean declaration in force — sign one first',
  'store.refusal.CONV_IN_CLEAN_BAY': 'Ordinary bag: only in the red-tie bay',
  'store.refusal.POSITION_HAS_OTHER_LOT': 'This spot holds another lot — pick an empty spot',
  'store.refusal.SAME_PLACE': 'Already at this place',
  'store.refusal.NOT_A_BAY': 'Scan a bay label (not a spot)',
  'store.refusal.NOT_SAMPLE_STORE': 'Seals go only to the sample store',
  'store.refusal.NOT_YOUR_ROLL': 'Not from your replacement roll',
  'store.refusal.TAG_ALREADY_USED': 'This tag is already used',
  'store.refusal.OLD_TAG_NOT_HERE': 'This bag is not at this place — treat it as a lost tag',

  'store.load.stepTrip': 'Trip',
  'store.load.stepBed': 'Vehicle bed',
  'store.load.stepScan': 'Scan bags',
  'store.load.stepDispatch': 'Dispatch',
  'store.load.destination': 'Destination',
  'store.load.vehicleNo': 'Vehicle number',
  'store.load.driverName': 'Driver name',
  'store.load.driverPhone': 'Driver phone',
  'store.load.driverPhoneHint': '10 digits',
  'store.load.challanNo': 'Challan number',
  'store.load.challanHint': 'From the numbered challan book',
  'store.load.bedClean': 'Bed empty and clean, no chemicals or fertiliser?',
  'store.load.bedLocked': 'Bags are already scanned; remove them to change this answer.',
  'store.load.bedDirtyTitle': 'Brand bags cannot go on this vehicle',
  'store.load.bedDirtyBody': 'Get another vehicle, or load only ordinary (red-tie) bags.',
  'store.load.onlyOrdinary': 'Bed not clean: only ordinary (red-tie) bags will be accepted.',
  'store.load.bagsOn': 'Bags on',
  'store.load.samplesOn': 'Samples on',
  'store.load.scanBag': 'Bag tag',
  'store.load.scanSample': 'Sample seal',
  'store.load.scanToRemove': 'Scan the bag to remove',
  'store.load.list': 'On this vehicle',
  'store.load.totals': 'Totals',
  'store.load.copyTotals': 'Copy these totals onto the challan and hand it to the driver.',
  'store.load.partialTitle': 'Part-loaded lots',
  'store.load.partialConfirm': '{lot}: {left} {left:bag waits|bags wait} for the next vehicle',
  'store.load.handCount': 'Bags counted on vehicle',
  'store.load.handCountHint': 'Count by hand; must equal the bags scanned',
  'store.load.handCountMismatch': 'Does not match {scanned} scanned {scanned:bag|bags}',
  'store.load.photo': 'Vehicle photo',
  'store.load.photoHint': 'Number plate visible',
  'store.load.lockedTitle': 'Dispatch is locked',
  'store.load.dispatch': 'Dispatch',
  'store.load.dispatched': 'Trip {code} dispatched',
  'store.load.block.DESTINATION': 'Pick the destination',
  'store.load.block.SAME_PLACE': 'Destination is this same place',
  'store.load.block.VEHICLE_NO': 'Type the vehicle number',
  'store.load.block.DRIVER_NAME': 'Type the driver’s name',
  'store.load.block.DRIVER_PHONE': 'Driver phone needs 10 digits',
  'store.load.block.CHALLAN_NO': 'Type the challan number',
  'store.load.block.VEHICLE_CLEAN': 'Answer the vehicle bed check',
  'store.load.block.NO_BAGS': 'Scan at least one bag',
  'store.load.block.HAND_COUNT': 'Hand count must equal the bags scanned',
  'store.load.block.PHOTO': 'Take the vehicle photo',
  'store.load.block.PART_LOAD': 'Confirm each part-loaded lot',

  'store.receive.arriving': 'Arriving vehicle',
  'store.receive.noTrips': 'No vehicle is on its way to this godown in the phone’s list.',
  'store.receive.tripLine': 'From {from} · challan {challan} · {bags} {bags:bag|bags}',
  'store.receive.leftAt': 'Left {when}',
  'store.receive.notListed': 'Vehicle not in the list — scan and hold',
  'store.receive.heldTitle': 'The list for this vehicle has not reached the phone',
  'store.receive.heldBody':
    'Scan the bags anyway. Receive unlocks when the list arrives; pick the vehicle then and use these scans.',
  'store.receive.heldCount': 'Scans held',
  'store.receive.useHeld': 'Use {count} held {count:scan|scans}',
  'store.receive.stepTrip': 'Challan',
  'store.receive.stepScan': 'Scan off',
  'store.receive.stepWeigh': 'Check weights',
  'store.receive.stepClose': 'Close',
  'store.receive.trip': 'Trip',
  'store.receive.challanCheck': 'Challan number — does it match the driver’s paper?',
  'store.receive.scanBag': 'Bag tag or sample seal',
  'store.receive.extrasTitle': '{count} {count:bag|bags} not on the list',
  'store.receive.extrasBody': 'They are received and must be weighed; the office decides.',
  'store.receive.problemBags': 'Problem bag',
  'store.receive.problemBody':
    'Put these bags in the hold area and photograph each one. They are never received as ours.',
  'store.receive.untagged': 'Bag without a tag',
  'store.receive.untaggedShort':
    'A lot on this trip is short. Put the bag aside and use Torn bag / lost tag.',
  'store.receive.untaggedNoShort':
    'Nothing on this trip is short: the bag becomes an ordinary orphan. Put it in the red-tie bay and tell the supervisor.',
  'store.receive.weighTitle': '{count} {count:bag|bags} to weigh on the godown scale',
  'store.receive.weighBody': 'One bag at a time. Someone other than the gate weigher must weigh.',
  'store.receive.weigh': 'Weigh',
  'store.receive.nothingToWeigh': 'No bag to weigh yet — scan the bags off first.',
  'store.receive.paperDay': 'paper day: every bag',
  'store.receive.checkWeight': 'Check weight (kg)',
  'store.receive.scale': 'Scale: {name}',
  'store.receive.gateNet': 'Gate net: {kg}',
  'store.receive.expectedNet': 'Expected after drying: {kg} (drying {drying})',
  'store.receive.receiveNet': 'Net now (sack off): {kg}',
  'store.receive.within': 'Within tolerance ({diff}, allowed {tol})',
  'store.receive.outside': 'Outside tolerance ({diff}, allowed {tol})',
  'store.receive.outOfTolTitle': 'Weigh again',
  'store.receive.outOfTolBody':
    'The first weight {first} is outside the tolerance. Put the bag back on the scale and type the weight again. If it is still outside, every bag of this lot must be weighed.',
  'store.receive.confirmSecond': 'Save second weight',
  'store.receive.sameWeigherTitle': 'You weighed this bag at the gate',
  'store.receive.sameWeigherBody': 'Someone else must unlock the phone and weigh it.',
  'store.receive.vehicleEmpty': 'Vehicle empty',
  'store.receive.notScanned': '{count} not scanned off',
  'store.receive.emptyFirst': 'Mark the vehicle empty before marking bags missing.',
  'store.receive.missingHint': 'Tap Missing for each bag that was not on the vehicle.',
  'store.receive.missing': 'Missing',
  'store.receive.missingReason': 'Why is it missing?',
  'store.receive.lockedTitle': 'Receive is locked',
  'store.receive.block.NO_TRIP': 'The trip list has not arrived on the phone',
  'store.receive.block.NOT_EMPTY': 'Mark the vehicle empty',
  'store.receive.block.OPEN_ITEMS': '{open} {open:bag|bags} neither scanned nor marked missing',
  'store.receive.block.CHECK_WEIGHTS':
    '{weights} picked {weights:bag still needs|bags still need} a check weight',
  'store.receive.receive': 'Receive',
  'store.receive.done': 'Trip {code} received',
  'store.receive.doneShort': 'Trip {code} received short',

  'store.missingReason.NOT_ON_VEHICLE': 'Not on the vehicle',
  'store.missingReason.TAG_UNREADABLE': 'Tag cannot be read',
  'store.missingReason.DAMAGED_IN_TRANSIT': 'Damaged on the road',

  'store.place.label': 'Location label',
  'store.place.newLabel': 'New location label',
  'store.place.labelHint': 'If the label won’t scan, type the code printed under it.',
  'store.place.typed': 'Typed',
  'store.place.notDeclaredWarn': 'No clean declaration in force: brand bags will be refused here.',
  'store.place.cleanWarn': 'Clean bay: ordinary (red-tie) bags will be refused here.',
  'store.place.sampleStoreHint': 'Sample store: scan each seal.',
  'store.place.scanBag': 'Bag tag',
  'store.place.scanSeal': 'Sample seal',
  'store.place.lotsHere': 'Lot count at this place',
  'store.place.convBanner': 'Ordinary (red-tie) bags on this list — red-tie bay only.',
  'store.place.from': 'from {place}',
  'store.place.confirm': 'Confirm',
  'store.place.putAway': '{count} put away at {place}',
  'store.place.moved': '{count} moved to {place}',
  'store.place.onDock': '{count} {count:bag|bags} waiting on the dock',

  'store.declaration.bayLabel': 'Bay label',
  'store.declaration.due': '{count} {count:bay needs|bays need} a declaration',
  'store.declaration.validTo': 'valid to {date}',
  'store.declaration.photo': 'Photo of the signed form',
  'store.declaration.validUntil': 'Valid until',
  'store.declaration.form': 'Form',
  'store.declaration.notCleanTitle': 'These answers make the bay not clean',
  'store.declaration.notCleanBody':
    'Brand bags can no longer go here, and the {count} brand {count:bag|bags} already in it will be put on hold where they are.',
  'store.declaration.sign': 'Sign',
  'store.declaration.paperHint':
    'Sign the printed form with the same answers before you photograph it.',
  'store.declaration.signed': 'Declaration for {bay} signed',

  'store.count.requests': '{count} {count:count|counts} asked by the office',
  'store.count.seen': 'Seen',
  'store.count.extra': 'Not expected',
  'store.count.scanTag': 'Scan every tag here',
  'store.count.shortBy': 'short by {n}',
  'store.count.nothingExpected': 'The phone expects no bags here.',
  'store.count.extraTitle': 'Bags that should be somewhere else',
  'store.count.unknownTitle': 'Codes that are not live bags',
  'store.count.done': 'Count done',
  'store.count.matched': 'Count matches',
  'store.count.mismatch': 'Count does not match — the office will see it',
  'store.count.sent': 'Count saved',
  'store.count.sentBody': 'It syncs with the office. Found a torn bag or a bag without a tag here?',

  'store.replace.title': 'Torn bag / lost tag',
  'store.replace.hint': 'New sack, new tag from your roll',
  'store.replace.countFirst': 'First count the place where the bag was found.',
  'store.replace.countDone': 'Count done — continue',
  'store.replace.noShort': 'No lot is short here: an untagged sack becomes an ordinary orphan.',
  'store.replace.oldTag': 'Old tag',
  'store.replace.oldTagHint': 'Only if it is still on the bag',
  'store.replace.callSupervisor': 'Old tag lost: call the supervisor.',
  'store.replace.newTag': 'New tag',
  'store.replace.newTagHint': 'Must be from your replacement roll',
  'store.replace.sackType': 'Sack type',
  'store.replace.weight': 'New sack weight (kg)',
  'store.replace.newNet': 'New net (sack off): {kg}',
  'store.replace.heavier': 'Heavier than the old bag — the supervisor will check',
  'store.replace.candidate': 'Matches {tag} of {lot} ({kg})',
  'store.replace.noCandidate': 'No single match ({n} possible) — it will become an ordinary orphan',
  'store.replace.photo': 'Photo',
  'store.replace.crop': 'Crop',
  'store.replace.cutOldTag': 'Cut the old tag and keep it for the supervisor.',
  'store.replace.replace': 'Replace',
  'store.replace.done': 'Replacement saved',
  'store.replace.waitSupervisor': 'The new sack stays on hold here until a supervisor checks it.',
  'store.replace.recent': 'Replacements from this phone',
  'store.replace.lostTag': 'lost tag',
  'store.replace.statusPending': 'Waiting to sync.',
  'store.replace.statusReview': 'On hold, waiting for the supervisor to verify or reject.',
  'store.replace.statusOrphan':
    'Not proven: it becomes an ordinary orphan. Tie a red tie, scan it, and move it to the red-tie bay.',
  'store.replace.statusError': 'Sync problem — open Sync.',
  'store.replace.evidence.OLD_TAG': 'old tag scanned',
  'store.replace.evidence.COUNT_AND_WEIGHT': 'count and weight match',
  'store.replace.evidence.NONE': 'no proof',

  'store.redTieList': '{count} {count:bag needs|bags need} a red tie',
  'store.redTieNone': 'No bags need a red tie.',
  'store.redTieScan': 'Tag (red tie on)',
  'store.redTieNotListed': 'This bag is not on the red-tie list',
  'store.redTieDone': '{count} {count:bag|bags} marked ordinary',
  'store.redTieMoveNext': 'Now carry them to the red-tie bay {bay} and scan them there.',

  // Seva Mandir V1 store screens (alignment map V1-F5 boards), one language at a time.
  'store.v1.home.title': '{site} · Today',
  'store.v1.home.arriving': 'Vehicles on the way',
  'store.v1.home.none': 'No vehicle on the way',
  'store.v1.home.refresh': 'Update the list',
  'store.v1.home.form': 'Godown form',
  'store.v1.home.send': 'Send goods',
  'store.v1.home.dock': '{count} {count:bag|bags} to put in place',
  'store.v1.home.dockLine': 'Unloaded at the dock. Put them in a bay',
  'store.v1.more.putAway': 'Put away bags',
  'store.v1.more.putAwayLine': 'From the dock to a bay or spot',
  'store.v1.hold.line': 'Office stopped this lot: {reason}',
  'store.v1.hold.lineNoReason': 'Office stopped this lot. Ask the supervisor',
  'store.v1.hold.reason.storage': 'storage risk',
  'store.v1.hold.reason.paper': 'paper day, check pending',
  'store.v1.hold.reason.labFail': 'lab test failed',
  'store.v1.hold.reason.labTest': 'lab test pending',
  'store.v1.hold.reason.quality': 'quality check',
  'store.v1.hold.reason.moisture': 'too much moisture',
  'store.v1.hold.reason.weight': 'weight check',
  'store.v1.hold.reason.count': 'bag count does not match',
  'store.v1.hold.reason.run': 'problem in the machine run',
  'store.v1.more.title': 'Other jobs',
  'store.v1.more.move': 'Move bags',
  'store.v1.more.moveLine': 'From one place in the godown to another',
  'store.v1.more.count': 'Count a bay',
  'store.v1.more.countLine': 'Count the bags when the office asks',
  'store.v1.more.stock': 'What is where',
  'store.v1.more.stockLine': 'Scan a place or a bag to see',
  'store.v1.more.redTie': 'Red tie bags',
  'store.v1.more.redTieLine': 'Ordinary grain kept apart',
  'store.v1.more.replace': 'Torn bag',
  'store.v1.more.replaceLine': 'Put the grain in a new bag',
  'store.v1.more.issue': 'Bags to the run',
  'store.v1.more.issueLine': 'Give bags from the godown to the machine',
  'store.v1.put.title': 'Put bags in place',
  'store.v1.put.scanPlace': 'Scan the place label',
  'store.v1.put.placeFirst': 'First scan the label of the **bay or spot** where the bags will go',
  'store.v1.put.onDock': '{count} {count:bag|bags} unloaded, not yet in place',
  'store.v1.put.change': 'Change place',
  'store.v1.put.notDeclared': 'Godown form not filled: good bags cannot go here yet',
  'store.v1.put.placed': 'placed',
  'store.v1.put.removeLast': 'Remove last',
  'store.v1.put.band': '{grade} · {lot} · {n}/{m} here',
  'store.v1.put.samples': '{count} {count:sample|samples}',
  'store.v1.put.confirm': 'Bags placed',
  'store.v1.put.doneTitle': 'Bags placed',
  'store.v1.put.doneLine': '**{count}** {count:bag|bags} at **{place}**',
  'store.v1.put.doneChip': 'The godown list shows them in their place',
  'store.v1.home.receive': 'Unload',
  'store.v1.home.held': 'Vehicle …{digits}: {count} {count:bag|bags}, match pending',
  'store.v1.tripLine': '{village} · {count} {count:bag|bags}',
  'store.v1.ago.min': '{n} min',
  'store.v1.ago.hour': '{n} {n:hour|hours}',
  'store.v1.ago.day': '{n} {n:day|days}',
  'store.v1.cache.title': 'Download the godown list',
  'store.v1.cache.body':
    'This phone has no list of bags and vehicles yet. Connect to the internet and download it.',
  'store.v1.cache.download': 'Download',
  'store.v1.cache.offline': 'No internet',
  'store.v1.cache.stale': 'The list is old: update it when there is internet',
  'store.v1.cache.failed': 'Could not download. Try again.',
  'store.v1.saveFailed': 'Not saved on the phone. Try again.',
  'store.v1.tag': 'Tag {tag}',
  'store.v1.farmerNo': '{name} · No. {no}',
  'store.v1.typeTitle': 'Tag number',
  'store.v1.typeNotFound': 'No bag with this number. Scan the tag.',
  'store.v1.typePlaceTitle': 'Place number',
  'store.v1.typePlaceNotFound': 'No single place with this number. Pick it from the list below.',
  'store.v1.placeList.title': 'Or pick the place',
  'store.v1.placeList.line': '{code} · {count} {count:bag|bags}',
  'store.v1.supervisor': 'Supervisor',
  'store.v1.home': 'Home',
  'store.v1.band.good': 'Good grain',
  'store.v1.band.red': 'Red tie (ordinary)',
  'store.v1.lots': 'Lots',
  'store.v1.bags': 'Bags',
  'store.v1.kg': 'Grain kg',
  'store.v1.load.title': 'Load the vehicle',
  'store.v1.load.where': 'Where is it going?',
  'store.v1.load.change': 'Change',
  'store.v1.load.which': 'Which vehicle?',
  'store.v1.load.lastTime': 'Last time {date}',
  'store.v1.load.newVehicle': 'New vehicle',
  'store.v1.load.plate': 'Look at the **number plate** and pick',
  'store.v1.load.pickDest': 'Pick where it goes',
  'store.v1.load.pickVehicle': 'Pick the vehicle',
  'store.v1.load.typeTitle': 'Vehicle number',
  'store.v1.load.typeHint': 'As written on the number plate',
  'store.v1.load.noDest': 'No godown on the list. Update the list.',
  'store.v1.scanOn.title': 'Scan bags on',
  'store.v1.scanOn.hold': 'Show the tag',
  'store.v1.scanOn.on': 'on',
  'store.v1.scanOn.moreLots': '{count} more {count:lot|lots}',
  'store.v1.scanOn.lotsLine': '{full} full · {left} left',
  'store.v1.scanOn.lotsTitle': 'Lots on the vehicle',
  'store.v1.scanOn.remove': 'Remove bag',
  'store.v1.scanOn.done': 'Loading done',
  'store.v1.scanOn.first': "First bag's tag",
  'store.v1.fixLast.title': 'Remove the last bag?',
  'store.v1.fixLast.body': 'Scanned by mistake? Take it off the list.',
  'store.v1.fixLast.yes': 'Yes, remove',
  'store.v1.remove.hint': 'Show the tag of the bag to take off',
  'store.v1.remove.notOn': 'This bag is not on the vehicle',
  'store.v1.remove.removed': 'Tag {tag} removed',
  'store.v1.remove.done': 'Done',
  'store.v1.refused.load': 'Tag {tag} will not go on',
  'store.v1.refused.receive': 'Tag {tag} not taken',
  'store.v1.refused.what': 'What to do now',
  'store.v1.refused.loadStep1': 'Take the bag **off and keep it aside**',
  'store.v1.refused.loadStep2': '**Phone** the supervisor',
  'store.v1.refused.receiveStep1': 'Keep the bag **aside**',
  'store.v1.refused.receiveStep2': '**Tell** the supervisor',
  'store.v1.refused.notOurs': 'This bag is not ours',
  'store.v1.refused.gotIt': 'Got it',
  'store.v1.problem.title': 'Problem bag',
  'store.v1.why.NOT_AT_ORIGIN': 'Tag from another place',
  'store.v1.why.ALREADY_LOADED': 'Already on a vehicle',
  'store.v1.why.NOT_STORED': 'This bag cannot be sent',
  'store.v1.why.TAG_VOID': 'Cancelled tag',
  'store.v1.why.TAG_RETIRED': 'Old tag',
  'store.v1.why.TAG_UNKNOWN': 'Tag not on the list',
  'store.v1.why.TAG_WRONG_KIND': 'Not a bag tag',
  'store.v1.why.OTHER': 'Cannot be loaded',
  'store.v1.count.title': 'Count the bags by hand',
  'store.v1.count.label': 'Bags on the vehicle',
  'store.v1.count.type': 'Type the count',
  'store.v1.dispatch.title': 'Send the vehicle?',
  'store.v1.dispatch.going': 'Going to {place}',
  'store.v1.dispatch.handCount': 'Counted by hand',
  'store.v1.dispatch.countOff': 'Counted {count}, scanned {scanned}: count again',
  'store.v1.dispatch.locked': 'After dispatch you **cannot remove or add** bags',
  'store.v1.dispatch.yes': 'Yes, dispatch',
  'store.v1.dispatched.title': 'Vehicle dispatched',
  'store.v1.dispatched.line': '{bags} {bags:bag|bags} · {lots} {lots:lot|lots}',
  'store.v1.dispatched.rescan': 'Every bag is scanned again where it arrives',
  'store.v1.pick.title': 'Which vehicle came?',
  'store.v1.pick.unknown': 'Vehicle not on the list',
  'store.v1.pick.matched': '{count} {count:bag|bags} already scanned',
  'store.v1.unknown.later': 'Unload, **match later**',
  'store.v1.unknown.digits': 'Last 4 digits of the vehicle number',
  'store.v1.unknown.wait': 'Type 4 digits',
  'store.v1.scanOff.title': 'Scan bags off',
  'store.v1.scanOff.off': 'off',
  'store.v1.scanOff.band': '{grade} · {lot} · {n}/{m} off',
  'store.v1.scanOff.receive': 'Receive',
  'store.v1.scanOff.first': "First bag's tag",
  'store.v1.held.vehicle': 'Vehicle …{digits}',
  'store.v1.held.later': 'List later',
  'store.v1.held.saved': 'Tag {tag} saved · match pending',
  'store.v1.held.allOff': 'All off',
  'store.v1.short.title': 'Receive? {count} {count:bag|bags} short',
  'store.v1.short.off': 'Off {n} / {m}',
  'store.v1.short.look': 'Look under the tarpaulin, in the **cabin**',
  'store.v1.short.warn': 'After "Yes" {count:this bag|these bags} will count as **missing**',
  'store.v1.short.back': 'Back, look more',
  'store.v1.short.yes': 'Yes, receive',
  'store.v1.received.title': 'Received',
  'store.v1.received.bags': '**{count}** {count:bag|bags} found',
  'store.v1.received.lots': '{count} {count:lot|lots} now in **{place}**',
  'store.v1.received.missing': '{count} {count:bag|bags} missing: the office will look',
  'store.v1.form.title': 'Godown form',
  'store.v1.form.one': 'One form for the whole godown',
  'store.v1.form.step1': '**Sign** the printed form',
  'store.v1.form.step2': 'Take a **photo** of the signed form',
  'store.v1.form.step3': 'Pick the **answers** written on the form',
  'store.v1.form.paper': 'Signed form',
  'store.v1.form.taken': 'Photo taken',
  'store.v1.form.again': 'Again',
  'store.v1.form.take': 'Take photo',
  'store.v1.form.yes': 'Yes',
  'store.v1.form.no': 'No',
  'store.v1.form.metres': '{n} m',
  'store.v1.form.sign': 'Submit the form',
  'store.v1.form.waitPhoto': 'Photo of the form',
  'store.v1.form.waitAnswers': 'Pick the answers',
  'store.v1.form.inForce': 'Form in force until {date}',
  'store.v1.signed.title': '{place} form submitted',
  'store.v1.signed.until': 'Valid until: {date}',
  'store.v1.signed.whole': 'For the whole godown',
  'store.v1.form.noBays': 'This godown has no storage bay yet. Ask the office.',
} as const

export type StoreMessageKey = keyof typeof storeEn
